import {useMemo} from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {GridColDef} from "@mui/x-data-grid";
import {useNavigate, useSearchParams} from "react-router-dom";
import {Box, Chip, Colors, IconButton, Text} from "@bounce/shared/ui";
import {Button, ListDataGrid, Tooltip, sortedByLocale} from "../../ui/components";
import {AccountCountInfoTooltip} from "../../components/tooltips";
import {useGetAudiences} from "../../apis/requests/audiences/getAudiences";
import {AudienceRow} from "../../apis/requests/audiences/types";
import {toQueryParams} from "../../apis/requests/queryParams";
import {prettifyCurrencyRounded} from "@bounce/shared/helpers";
import {reachUrls} from "../../layout/menu-items";
import {sumTemplatesPerChannel} from "../audiences/templatesPerChannel";
import {SegmentNameChip} from "../segments/SegmentNameChip";

const ID_COLUMN_WIDTH = 100;

const sameIds = (a: number[], b: number[]): boolean => a.length === b.length && a.every((id) => b.includes(id));

const CampaignDetailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {data, isLoading, error} = useGetAudiences();
  const audiences = data ?? [];

  const segmentIds = useMemo(() => searchParams.getAll("segment_ids").map(Number), [searchParams]);

  const campaignAudiences = useMemo<AudienceRow[]>(
    () => audiences.filter((row) => sameIds(row.campaign_segment_ids, segmentIds)),
    [audiences, segmentIds]
  );

  const campaign = campaignAudiences[0];
  const accountCount = campaignAudiences.reduce((total, row) => total + row.account_count, 0);
  const faceValue = campaignAudiences.reduce((total, row) => total + row.total_balance, 0);
  const templateCount = campaign ? sumTemplatesPerChannel(campaign.num_of_templates_per_channel) : 0;

  const getAudienceUrl = (row: AudienceRow) =>
    `${reachUrls.audienceDetail}${toQueryParams("segment_ids", row.audience_segment_ids)}`;

  const columns = useMemo<GridColDef<AudienceRow>[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: ID_COLUMN_WIDTH,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({row}) => (
          <Tooltip title={row.id} placement="top" disableHoverListener={!row.id}>
            <Box sx={{minWidth: 0, maxWidth: "100%"}}>
              <Text
                size={14}
                sx={{color: Colors.grey._700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
              >
                {row.id ?? "—"}
              </Text>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "audience_segment_names",
        headerName: "Audience",
        flex: 4,
        valueGetter: (_value, row) => sortedByLocale(row.audience_segment_names).join(", "),
        renderCell: ({row}) => (
          <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", py: 1}}>
            {row.audience_segment_names.length === 0 ? (
              <Chip size_="small" variant_="inherit" label="Unassigned" />
            ) : (
              row.audience_segment_names.map((name, i) => (
                <SegmentNameChip
                  key={i}
                  name={name}
                  variant="secondary"
                />
              ))
            )}
          </Box>
        ),
      },
      {
        field: "account_count",
        headerName: "Accounts",
        flex: 1,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
            <Text size={12} variant="inter600" sx={{color: Colors.grey._700}}>
              Accounts
            </Text>
            <AccountCountInfoTooltip />
          </Box>
        ),
        renderCell: ({row}) => (
          <Text size={14} sx={{color: Colors.grey._1000}}>
            {row.account_count.toLocaleString()}
          </Text>
        ),
      },
      {
        field: "total_balance",
        headerName: "Face value",
        flex: 1,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({row}) => (
          <Text size={14} sx={{color: Colors.grey._1000}}>
            {prettifyCurrencyRounded(row.total_balance)}
          </Text>
        ),
      },
    ],
    []
  );

  if (isLoading) {
    return (
      <Box sx={{p: 4}}>
        <Text size={14}>Loading…</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{p: 4}}>
        <Text size={14}>Failed to load campaign.</Text>
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box sx={{p: 4, display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start"}}>
        <Text size={14}>Campaign not found.</Text>
        <Button buttonType="outlined-primary" size="small" onClick={() => navigate(reachUrls.campaigns)}>
          Back to campaigns
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", height: "100%"}}>
      <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mb: 3}}>
        <IconButton
          onClick={() => navigate(reachUrls.campaigns)}
          sx={{
            border: `1px solid ${Colors.tertiary._300}`,
            borderRadius: "8px",
            padding: "6px",
            color: Colors.grey._700,
            "&:hover": {backgroundColor: Colors.tertiary._100},
          }}
        >
          <ArrowBackIcon sx={{fontSize: 20}} />
        </IconButton>
        <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center"}}>
          {campaign.campaign_segment_names.length === 0 ? (
            <Chip size_="small" variant_="inherit" label="Default" />
          ) : (
            campaign.campaign_segment_names.map((name, i) => (
              <SegmentNameChip
                key={i}
                name={name}
                variant="inherit"
              />
            ))
          )}
        </Box>
      </Box>

      <Box sx={{display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap", mb: 3}}>
        <Text size={14} sx={{color: Colors.grey._700}}>
          {accountCount.toLocaleString()} accounts
        </Text>
        <Text size={14} sx={{color: Colors.grey._700}}>
          {prettifyCurrencyRounded(faceValue)} face value
        </Text>
        <Text size={14} sx={{color: Colors.grey._700}}>
          {campaignAudiences.length.toLocaleString()} audiences
        </Text>
        <Text size={14} sx={{color: Colors.grey._700}}>
          {templateCount.toLocaleString()} templates
        </Text>
      </Box>

      <Text size={16} variant="interDisplay600" sx={{color: Colors.grey._700, mb: 1.5}}>
        Audiences
      </Text>
      <ListDataGrid
        rows={campaignAudiences}
        columns={columns}
        getRowId={(row) => row.id}
        getRowUrl={getAudienceUrl}
        pageSizeOptions={[10, 25, 50, 100]}
        noRowsText="No audiences in this campaign yet"
      />
    </Box>
  );
};

export default CampaignDetailPage;
